import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Terminal from '../components/terminal/Terminal';
import SemanticGPUBrowse from '../components/gpu/SemanticGPUBrowse';
import QuotaRequestModal from '../components/gpu/QuotaRequestModal';
import IdleWarningBanner from '../components/IdleWarningBanner';
import { useIdleNotification } from '../contexts/IdleNotificationContext';
import { getApiUrl, extractErrorMessage } from '../utils/api';
import { getInstanceDisplayState, getStatusVisuals } from '../utils/instanceStatus';
import { createWebSocketConnection, WebSocketService } from '../services/WebSocketService';
import Draggable from 'react-draggable';
import type { SemanticGPUResponse } from '../types/gpu';

interface GPUInstance {
  provider: string;
  region: string;
  instance_type: string;
  instance_name: string;
  vcpus: number;
  memory_gb: number;
  gpu_count: number;
  gpu_type: string;
  gpu_memory_gb: number;
  price_per_hour_ondemand?: number;
  price_per_hour_spot?: number;
  price_per_gpu_hour?: number;
  cost_per_vcpu_hour?: number;
  memory_per_gpu_gb?: number;
  network_performance?: string;
  storage_type?: string;
  status: string;
  use_cases: string[];
}

interface ComputeFilters {
  providers?: string[];
  region?: string;
  gpu_type?: string;
  min_vcpus?: number;
  max_price_per_hour?: number;
}

type SortField = 'provider' | 'gpu_type' | 'instance_type' | 'vcpus' | 'memory_gb' | 'price_per_hour_ondemand' | 'price_per_hour_spot' | 'price_per_gpu_hour';
type SortDirection = 'asc' | 'desc';

/**
 * LaunchedInstance represents a GPU instance record from the database.
 * IMPORTANT: The 'id' field is the database primary key (integer) and is the ONLY
 * identifier that should be used for API operations like terminal creation.
 * The 'instance_id' field is the cloud provider's instance identifier (string)
 * and should only be used for display or cloud-specific operations.
 */
interface LaunchedInstance {
  // id field removed - using instance_id as primary identifier
  provider: string;
  instance_id: string;  // Cloud provider's instance ID - PRIMARY IDENTIFIER
  instance_type: string;
  region: string;
  session_id: string;
  project_name?: string;
  status: string;
  public_ip?: string;
  private_ip?: string;
  launch_time?: string;
  runtime_hours?: number;
  hourly_cost?: number;
  total_cost?: number;
  connection_info?: {
    ssh_command?: string;
    web_interface?: string;
    jupyter_url?: string;
    status_message?: string;
    operation_id?: string;
    zone?: string;
    project_id?: string;
    projectId?: string;
    project?: string;
    instance_name?: string;
    availability_zone?: string;
    region?: string;
    terminal_access?: string;
  };
  error_message?: string;
  user_error_explanation?: string;
  error_type?: string | null;
  // terminal_ready: null=unchecked, true=ready, false=not ready
  // When status=running but terminal_ready is null/false, show "Starting..."
  terminal_ready?: boolean | null;
  jupyter_ready?: boolean | null;
}

/**
 * Validates that an instance has a valid database ID.
 * Throws a clear error if the ID is missing or invalid.
 * @param instance - The instance to validate
 * @returns The validated database ID
 */
// Function removed - we now use CSP instance_id instead of database ID

// Deterministic human-readable fallback for launch failures when Bedrock AI explanation is unavailable.
// Keyed off structured error_type from the backend; falls back to the raw message.
function fallbackErrorMessage(errorType: string | null | undefined, rawMessage: string | null | undefined): string {
  switch (errorType) {
    case 'capacity_exhausted':
      return 'This region is temporarily full. Try one of the alternatives below.';
    case 'quota_exceeded':
      return "You've hit your quota limit for this instance type. Request more capacity below.";
    case 'vpc_pool_exhausted':
      return 'No network slots available in this region right now. Try another region.';
    case 'rate_limited':
      return 'Too many requests recently. Wait a minute and retry.';
    case 'image_unavailable':
      return 'No image is available for this instance in this region. Try another region.';
    default:
      return rawMessage || 'An unexpected error occurred.';
  }
}

export default function ComputePage() {
  const [instances, setInstances] = useState<GPUInstance[]>([]);
  const [launchedInstances, setLaunchedInstances] = useState<LaunchedInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(null);
  const [refreshingLaunched, setRefreshingLaunched] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState<string | null>(null);
  const [failureToast, setFailureToast] = useState<{ message: string; id: string } | null>(null);

  // Launch modal state
  const [showLaunchModal, setShowLaunchModal] = useState(false);
  const [selectedInstance, setSelectedInstance] = useState<GPUInstance | null>(null);
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);
  const [capacityAlternatives, setCapacityAlternatives] = useState<Array<{
    provider: string; instance_type: string; region: string;
    gpu_model: string; gpu_count: number; price_per_hour: number | null;
    last_capacity_event_age_min?: number | null;
    health?: 'healthy' | 'degraded';
  }>>([]);
  const [launchErrorType, setLaunchErrorType] = useState<string | null>(null);
  const [failingRegionAgeMin, setFailingRegionAgeMin] = useState<number | null>(null);
  const [quotaModalContext, setQuotaModalContext] = useState<{
    provider: string; instanceType: string; region: string; displayName: string;
  } | null>(null);
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [pricingType, setPricingType] = useState<'ondemand' | 'spot'>('ondemand');
  const [projectName, setProjectName] = useState('');
  const [diskSizeGb, setDiskSizeGb] = useState<number | null>(null);
  const [launchProgress, setLaunchProgress] = useState<string[]>([]);
  const [launchSteps, setLaunchSteps] = useState<{id: string; label: string; status: 'pending' | 'active' | 'done' | 'error'}[]>([]);
  const [launchBillingInfo, setLaunchBillingInfo] = useState<{prepaid?: string; runtime?: string}>({});
  const [priceEstimate, setPriceEstimate] = useState<{
    compute_price_per_hour: number;
    disk_price_per_hour: number;
    disk_size_gb: number;
    disk_type: string;
    total_price_per_hour: number;
    spot_available?: boolean;
  } | null>(null);
  // WebSocket is handled per launch now, not stored as state
  
  // Terminal and connection state
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState<LaunchedInstance | null>(null);
  const [terminalSessions, setTerminalSessions] = useState<Map<string, any>>(new Map());
  
  // Instance details modal
  const [showInstanceModal, setShowInstanceModal] = useState(false);
  const [selectedLaunchedInstance, setSelectedLaunchedInstance] = useState<LaunchedInstance | null>(null);

  // GPU details modal
  const [showGPUDetailsModal, setShowGPUDetailsModal] = useState(false);
  const [selectedGPUDetails, setSelectedGPUDetails] = useState<SemanticGPUResponse | null>(null);

  // Filters
  
  // View mode
  const [viewMode, setViewMode] = useState<'semantic' | 'launched'>('semantic');
  
  // Sorting state
  const [sortField, setSortField] = useState<SortField>('price_per_hour_ondemand');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  
  // Terminal state
  const [terminalAvailable, setTerminalAvailable] = useState<boolean>(false);
  const [checkingTerminal, setCheckingTerminal] = useState<boolean>(false);

  // Track which instances are being polled for terminal readiness
  const terminalReadinessPollingRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const TERMINAL_READY_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes
  const TERMINAL_POLL_INTERVAL_MS = 3000; // 3 seconds


  // Background progress notification
  const [backgroundProgress, setBackgroundProgress] = useState<string[]>([]);
  const [showProgressNotification, setShowProgressNotification] = useState(false);

  // Data freshness tracking
  const [catalogHealth, setCatalogHealth] = useState<any>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  // Sync status tracking
  const [syncStatus, setSyncStatus] = useState<{
    aws: { success: boolean; error: string | null; last_sync: string | null; instances_synced: number };
    gcp: { success: boolean; error: string | null; last_sync: string | null; instances_synced: number };
  } | null>(null);

  // Idle shutdown tracking
  const [idleStatus, setIdleStatus] = useState<Record<string, { idle_minutes: number | null; shutdown_deadline: string | null }>>({});
  const { idleWarnings, dismissWarning, balanceWarning, dismissBalanceWarning } = useIdleNotification();

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Research-feed → compute deep link. When the user clicks "Launch on T4 →"
  // on a post-generate toast, we arrive here with ?tier=<id>&notebook=<s3_key>.
  // We stash the notebook key under a "next launch" sentinel — NotebookLauncher
  // already reads instance-keyed entries after a launch (see the existing
  // `pending_notebook_for_${instanceId}` flow); we promote the sentinel once
  // the instance id is known. The tier hint is forwarded to the launcher via
  // sessionStorage too, so the modal can preselect the right row without
  // threading props through several components.
  // Unknown tier strings never break the page — we log and fall through.
  useEffect(() => {
    const tier = searchParams.get('tier');
    const notebook = searchParams.get('notebook');
    if (!tier && !notebook) return;
    if (notebook) {
      sessionStorage.setItem('pending_notebook_for_next_launch', notebook);
    }
    if (tier) {
      sessionStorage.setItem('pending_tier_hint_for_next_launch', tier.toLowerCase());
    }
    // Clear the query params so a refresh doesn't re-trigger the stash.
    // Keep other query params intact in case future code adds them.
    const next = new URLSearchParams(searchParams);
    next.delete('tier');
    next.delete('notebook');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // Calculate data freshness status
  const getDataFreshness = (lastUpdated: string | null) => {
    if (!lastUpdated) return { status: 'unknown', color: 'gray', text: 'Unknown' };

    const now = new Date();
    const updated = new Date(lastUpdated);
    const hoursOld = (now.getTime() - updated.getTime()) / (1000 * 60 * 60);

    if (hoursOld < 1) {
      return { status: 'fresh', color: 'green', text: `${Math.round(hoursOld * 60)} minutes ago` };
    } else if (hoursOld < 6) {
      return { status: 'stale', color: 'yellow', text: `${Math.round(hoursOld)} hours ago` };
    } else {
      return { status: 'very_stale', color: 'red', text: `${Math.round(hoursOld)} hours ago` };
    }
  };

  const fetchInstances = async (filters: ComputeFilters = {}) => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('authToken');
      if (!token) {
        navigate('/login');
        return;
      }

      // Use GPU catalog API for instant cached data
      const response = await fetch(`${getApiUrl()}/api/gpu/discover`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch GPU instances: ${response.status}`);
      }

      const data = await response.json();
      // Map the semantic GPU response to the expected format
      const mappedInstances = data.map((gpu: any) => ({
        instance_type: gpu.provider_instance_id,
        region: gpu.region,
        gpu_type: gpu.gpu_model_name,
        gpu_count: gpu.gpu_count,
        pricing: {
          on_demand: gpu.on_demand_price_per_hour,
          spot: gpu.spot_price_per_hour
        },
        availability_zones: gpu.availability_zones,
        provider: gpu.provider,
        vcpus: gpu.vcpus,
        memory_gb: gpu.system_memory_gib,
        storage_gb: gpu.storage_size_gb,
        network_bandwidth_gbps: gpu.network_bandwidth_gbps,
        last_updated: gpu.pricing?.last_updated || gpu.specs_last_updated
      }));

      setInstances(mappedInstances);
      setLastRefresh(new Date());

      // Also fetch catalog health for freshness indicators
      try {
        const healthResponse = await fetch(`${getApiUrl()}/api/gpu/catalog/health`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (healthResponse.ok) {
          const health = await healthResponse.json();
          setCatalogHealth(health.background_service?.services?.gpu_refresh?.catalog_health || health.catalog_health);
        }
      } catch {
        // Catalog health is non-critical
      }

    } catch (err: any) {
      console.error('Error fetching GPU instances:', err);
      setError(err.message || 'Failed to fetch GPU instances');
      setInstances([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchLaunchedInstances = async (refresh: boolean = false) => {
    if (refresh) {
      setRefreshingLaunched(true);
      setRefreshMessage(null);
    }
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const url = refresh
        ? `${getApiUrl()}/api/compute/instances/user?refresh=true`
        : `${getApiUrl()}/api/compute/instances/user`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Handle 401 Unauthorized - token is invalid or expired
      if (response.status === 401) {
        console.error('[fetchLaunchedInstances] Authentication failed - clearing token and redirecting to login');
        localStorage.removeItem('authToken');
        navigate('/login');
        return;
      }

      if (response.ok) {
        const data = await response.json();
        const instances = data.instances || [];
        const syncStatus = data.sync_status || null;

        // Update sync status
        if (syncStatus) {
          setSyncStatus(syncStatus);
        }

        if (refresh) {
          setRefreshMessage(`✓ Refreshed ${instances.length} instance${instances.length !== 1 ? 's' : ''}`);
          setTimeout(() => setRefreshMessage(null), 3000);
        }
        
        // Validate and update status for launching instances
        const validatedInstances: LaunchedInstance[] = [];
        
        for (const instance of instances) {
          // Validate that each instance has a valid CSP instance ID
          try {
            if (!instance.instance_id) {
              throw new Error(`Instance missing CSP instance_id: ${JSON.stringify(instance)}`);
            }
            
            // If instance is launching, try to get updated status
            if (instance.status === 'launching' || instance.status === 'LAUNCHING') {
              try {
                // Get updated status from backend
                const statusResponse = await fetch(`${getApiUrl()}/api/compute/instances/${instance.instance_id}/status`, {
                  headers: {
                    'Authorization': `Bearer ${token}`
                  }
                });
                
                if (statusResponse.ok) {
                  const statusData = await statusResponse.json();
                  // Ensure the updated data also has a valid instance_id
                  if (!statusData.instance_id) {
                    console.error(`[fetchLaunchedInstances] Status update response missing instance_id for instance ${instance.instance_id}`);
                    statusData.instance_id = instance.instance_id; // Preserve the original instance_id
                  }
                  validatedInstances.push(statusData);
                  continue;
                }
              } catch (err) {
                console.error(`Error updating status for instance ${instance.instance_id}:`, err);
              }
            }
            
            validatedInstances.push(instance);
          } catch (validationError) {
            console.error(`[fetchLaunchedInstances] Skipping invalid instance:`, validationError);
            // Show user-friendly error message
            const errorMessage = validationError instanceof Error 
              ? validationError.message 
              : 'Unknown validation error';
            setError(`Data validation error: ${errorMessage}`);
          }
        }
        
        setLaunchedInstances(prev => {
          if (prev.length === 0) return validatedInstances;
          // Detect newly failed instances and show toast
          for (const inst of validatedInstances) {
            if (inst.status === 'error') {
              const existing = prev.find(p => p.instance_id === inst.instance_id);
              if (existing && existing.status !== 'error') {
                // Prefer Bedrock's AI explanation; fall back to a deterministic
                // message derived from error_type when Bedrock is unavailable.
                const msg = inst.user_error_explanation
                  || fallbackErrorMessage(inst.error_type, inst.error_message);
                setFailureToast({ message: msg, id: inst.instance_id });
                setTimeout(() => setFailureToast(null), 10000);
              }
            }
          }
          return validatedInstances.map(inst => {
            const existing = prev.find(p => p.instance_id === inst.instance_id);
            // Don't downgrade terminal_ready from true to null
            // (race: local terminal poll confirmed before this fetch response was computed)
            if (existing?.terminal_ready === true && inst.terminal_ready == null) {
              return { ...inst, terminal_ready: true };
            }
            return inst;
          });
        });
        return validatedInstances;
      }
    } catch (err) {
      console.error('Error fetching launched instances:', err);
      if (refresh) {
        setRefreshMessage('✗ Failed to refresh');
        setTimeout(() => setRefreshMessage(null), 3000);
      }
    } finally {
      if (refresh) {
        setRefreshingLaunched(false);
      }
    }
  };

  const handleLaunch = (instance: GPUInstance) => {
    setSelectedInstance(instance);
    setShowLaunchModal(true);
    setLaunchError(null);
    setLaunchErrorType(null);
    setCapacityAlternatives([]);
    setFailingRegionAgeMin(null);
    setQuotaModalContext(null);
    setPricingType('ondemand');
    setProjectName('');
    setDiskSizeGb(null);
  };

  const wsServiceRef = useRef<WebSocketService | null>(null);
  const statusPollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const INITIAL_LAUNCH_STEPS = [
    { id: 'config', label: 'Validating configuration', status: 'pending' as const },
    { id: 'provision', label: 'Provisioning instance', status: 'pending' as const },
    { id: 'billing', label: 'Processing billing', status: 'pending' as const },
    { id: 'ready', label: 'Instance ready', status: 'pending' as const },
  ];

  const advanceToStep = (stepId: string, status: 'active' | 'done' | 'error' = 'active') => {
    setLaunchSteps(prev => {
      const targetIdx = prev.findIndex(s => s.id === stepId);
      if (targetIdx < 0) return prev;
      return prev.map((step, idx) => {
        if (idx < targetIdx) return { ...step, status: 'done' as const };
        if (idx === targetIdx) return { ...step, status };
        return step;
      });
    });
  };

  const mapWsMessageToStep = (msg: string) => {
    if (msg.includes('cloud provider') || msg.includes('security') || msg.includes('image') || msg.includes('configuration') || msg.includes('Selecting optimal')) {
      advanceToStep('config', 'active');
    } else if (msg.includes('network') || msg.includes('Setting up')) {
      advanceToStep('provision', 'active');
    } else if (msg.includes('launched successfully') || msg.includes('Initializing')) {
      advanceToStep('provision', 'done');
    } else if (msg.includes('failed') || msg.includes('error') || msg.includes('Error')) {
      // Find the current active step and mark it as error
      setLaunchSteps(prev => prev.map(s => s.status === 'active' ? { ...s, status: 'error' as const } : s));
    }
  };

  const launchInstance = async () => {
    if (!selectedInstance) return;

    try {
      setLaunching(true);
      setLaunchError(null);
      setCapacityAlternatives([]);
      setLaunchProgress([]);
      setBackgroundProgress([]);
      setLaunchSteps(INITIAL_LAUNCH_STEPS.map(s => ({...s})));
      setLaunchBillingInfo({});
      
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token');
      }
      
      let launchedInstanceId: string | null = null;
      
      // Decode user ID from JWT token for WebSocket connection
      let userId = '1'; // Default fallback
      
      try {
        const { jwtDecode } = await import('jwt-decode');
        const decoded = jwtDecode(token) as any;
        userId = decoded.sub || decoded.user_id || decoded.id || '1';
      } catch (e) {
        console.error('Failed to decode JWT token:', e);
        // Try to get from user object as fallback
        const userStr = localStorage.getItem('user');
        if (userStr) {
          try {
            const user = JSON.parse(userStr);
            userId = user.id || user.user_id || '1';
          } catch (parseError) {
            console.error('Failed to parse user from localStorage:', parseError);
          }
        }
      }
      
      // Create WebSocket connection for real-time updates (non-blocking)
      wsServiceRef.current = createWebSocketConnection(`/ws/launch-status/${userId}`, {
        reconnect: true,
        maxReconnectAttempts: 3,
        enableQueuing: false
      });
      
      // Set up WebSocket message handler
      wsServiceRef.current.onMessage((message) => {
        if (message.type === 'launch_update' || message.type === 'launch_status') {
          // Backend sends {type, status, message} at top level (not nested under data)
          const wsMsg = message as any;
          const msg: string = wsMsg.message || message.data?.message || message.data || 'Processing...';
          const wsStatus: string = wsMsg.status || message.data?.status || 'info';

          // Advance stepper based on message content
          mapWsMessageToStep(msg);

          // Also update background progress if modal is closed
          if (!showLaunchModal) {
            setBackgroundProgress(prev => [...prev, msg]);
            setShowProgressNotification(true);
          }

          if (wsStatus === 'error') {
            setLaunchError(msg);
          }

          // Check if launch is complete
          if (msg.includes('Instance is ready') || msg.includes('successfully launched')) {
            advanceToStep('ready', 'done');

            // Clear polling if it's running
            if (statusPollingIntervalRef.current) {
              clearInterval(statusPollingIntervalRef.current);
              statusPollingIntervalRef.current = null;
            }

            // Refresh launched instances
            fetchLaunchedInstances();

            // Clear progress after a delay
            setTimeout(() => {
              setBackgroundProgress([]);
              setShowProgressNotification(false);
            }, 3000);
          }
        }
      });
      
      // Connect WebSocket asynchronously (non-blocking)
      wsServiceRef.current.connectAsync();

      // Check payment status before launching
      const paymentResponse = await fetch(`${getApiUrl()}/api/stripe/payment-status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (paymentResponse.ok) {
        const paymentData = await paymentResponse.json();
        if (!paymentData.has_payment_method || paymentData.setup_required) {
          setLaunchError(`${paymentData.message || 'Payment method required.'} Please go to Billing Settings to add a payment method.`);
          return;
        }
      } else {
        setLaunchError('Unable to verify payment method. Please go to Billing Settings to check your payment setup.');
        return;
      }

      // Launch the instance (this proceeds regardless of WebSocket status)
      advanceToStep('config', 'active');
      
      const response = await fetch(`${getApiUrl()}/api/compute/launch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          provider: selectedInstance.provider,
          instance_type: selectedInstance.instance_type,
          region: selectedInstance.region,
          project_name: projectName,
          spot_instance: pricingType === 'spot',
          max_price_per_hour: pricingType === 'spot'
            ? selectedInstance.price_per_hour_spot
            : selectedInstance.price_per_hour_ondemand,
          disk_size_gb: diskSizeGb
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        setLaunchErrorType(errorData.error_type ?? null);
        const backendMessage = extractErrorMessage(errorData, '');
        if (errorData.error_type === 'capacity_exhausted') {
          setCapacityAlternatives(errorData.alternatives || []);
          setFailingRegionAgeMin(errorData.failing_region_age_min ?? null);
          throw new Error(backendMessage || fallbackErrorMessage('capacity_exhausted', null));
        }
        if (errorData.error_type === 'quota_exceeded') {
          setQuotaModalContext({
            provider: errorData.provider ?? selectedInstance.provider,
            instanceType: errorData.instance_type ?? selectedInstance.instance_type,
            region: errorData.region ?? selectedInstance.region,
            displayName: selectedInstance.gpu_type || selectedInstance.instance_type,
          });
          throw new Error(backendMessage || fallbackErrorMessage('quota_exceeded', null));
        }
        throw new Error(
          backendMessage || fallbackErrorMessage(errorData.error_type, null)
        );
      }

      const launchData = await response.json();

      // Backend returns HTTP 200 with success=false when the provider reports
      // a failure without throwing (e.g., status=ERROR in the LaunchedInstance).
      if (launchData.success === false) {
        throw new Error(launchData.message || 'Instance launch failed. Check the dashboard for details.');
      }

      launchedInstanceId = launchData.instance_id || launchData.id;

      advanceToStep('billing', 'active');

      if (launchData.initial_charge_usd) {
        const prepaid = `$${launchData.initial_charge_usd.toFixed(2)} prepaid (10 min minimum)${
          launchData.balance_after != null ? ` — Balance: $${launchData.balance_after.toFixed(2)}` : ''
        }`;
        setLaunchBillingInfo(prev => ({ ...prev, prepaid }));
      }

      if (launchData.estimated_runtime_hours) {
        setLaunchBillingInfo(prev => ({
          ...prev,
          runtime: `~${launchData.estimated_runtime_hours}h estimated runtime with current balance`
        }));
      }

      advanceToStep('billing', 'done');
      
      // If WebSocket isn't connected after a short delay, start polling for status updates
      setTimeout(() => {
        if (!wsServiceRef.current?.isConnected && launchedInstanceId) {
          
          // Poll for instance status
          statusPollingIntervalRef.current = setInterval(async () => {
            try {
              const statusResponse = await fetch(
                `${getApiUrl()}/api/compute/instances/${launchedInstanceId}/status`,
                {
                  headers: {
                    'Authorization': `Bearer ${token}`
                  }
                }
              );
              
              if (statusResponse.ok) {
                const statusData = await statusResponse.json();
                const stateMsg = `Instance state: ${statusData.state || statusData.status}`;
                
                if (statusData.state === 'running' || statusData.status === 'running') {
                  advanceToStep('ready', 'done');

                  // Stop polling
                  if (statusPollingIntervalRef.current) {
                    clearInterval(statusPollingIntervalRef.current);
                    statusPollingIntervalRef.current = null;
                  }

                  // Refresh instances
                  fetchLaunchedInstances();
                } else if (statusData.state === 'pending' || statusData.state === 'launching' ||
                           statusData.status === 'pending' || statusData.status === 'launching') {
                  advanceToStep('provision', 'active');
                }
              }
            } catch (error) {
              console.error('Error polling instance status:', error);
            }
          }, 3000); // Poll every 3 seconds
          
          // Stop polling after 2 minutes
          setTimeout(() => {
            if (statusPollingIntervalRef.current) {
              clearInterval(statusPollingIntervalRef.current);
              statusPollingIntervalRef.current = null;
              // Status monitoring timed out — mark current active step as done
              setLaunchSteps(prev => prev.map(s => s.status === 'active' ? { ...s, status: 'done' as const } : s));
            }
          }, 120000);
        }
      }, 2000); // Check WebSocket connection after 2 seconds
      
      // Add a small delay to see the success message
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Close the modal and refresh instances
      setShowLaunchModal(false);
      setSelectedInstance(null);
      
      // Fetch the updated list of instances
      await fetchLaunchedInstances();
      
      // Switch to launched instances view to show the new instance
      setViewMode('launched');
      
    } catch (err: any) {
      console.error('Error launching instance:', err);
      setLaunchError(err.message || 'Failed to launch instance');
    } finally {
      setLaunching(false);
      
      // Clean up WebSocket and polling after a delay
      setTimeout(() => {
        if (wsServiceRef.current) {
          wsServiceRef.current.close();
          wsServiceRef.current = null;
        }
        if (statusPollingIntervalRef.current) {
          clearInterval(statusPollingIntervalRef.current);
          statusPollingIntervalRef.current = null;
        }
      }, 10000);
    }
  };

  const terminateInstance = async (instanceId: string) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.error('No auth token found');
        return;
      }

      const url = `${getApiUrl()}/api/compute/instances/${instanceId}/terminate`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        // Optimistically set status to terminating in the table
        setLaunchedInstances(prev => prev.map(inst =>
          inst.instance_id === instanceId ? { ...inst, status: 'terminating' } : inst
        ));
        // Refresh from server to get final state
        await fetchLaunchedInstances();
      } else {
        const errorText = await response.text();
        console.error('Failed to terminate instance. Status:', response.status, 'Error:', errorText);
      }
    } catch (err) {
      console.error('Error terminating instance:', err);
    }
  };

  const removeInstance = async (instanceId: string) => {
    if (!instanceId) {
      alert('Error: Instance ID is missing');
      return;
    }
    
    if (!window.confirm('Are you sure you want to remove this instance record? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const url = `${getApiUrl()}/api/compute/instances/${instanceId}`;

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await fetchLaunchedInstances();
      } else {
        const errorData = await response.json();
        alert(`Failed to remove instance: ${errorData.detail}`);
      }
    } catch (err) {
      console.error('Error removing instance:', err);
      alert('Failed to remove instance. Please try again.');
    }
  };

  const checkTerminalAvailability = async (instance: LaunchedInstance): Promise<{ available: boolean; terminal_ready: boolean; message?: string }> => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return { available: false, terminal_ready: false };

      const response = await fetch(`${getApiUrl()}/api/compute/instances/${instance.instance_id}/terminal/available`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        return {
          available: data.available,
          terminal_ready: data.terminal_ready,
          message: data.message
        };
      }
      return { available: false, terminal_ready: false };
    } catch (err) {
      console.error('Error checking terminal availability:', err);
      return { available: false, terminal_ready: false };
    }
  };

  // Start polling for terminal readiness for a specific instance
  const startTerminalReadinessPolling = (instanceId: string) => {
    // Don't start if already polling
    if (terminalReadinessPollingRef.current.has(instanceId)) return;

    const startTime = Date.now();

    const poll = async () => {
      // Check timeout
      if (Date.now() - startTime > TERMINAL_READY_TIMEOUT_MS) {
        console.warn(`Terminal readiness polling timed out for ${instanceId}`);
        stopTerminalReadinessPolling(instanceId);
        // Update instance to show timeout error
        setLaunchedInstances(prev => prev.map(inst =>
          inst.instance_id === instanceId
            ? { ...inst, terminal_ready: false, error_message: 'Terminal initialization timed out' }
            : inst
        ));
        return;
      }

      const instance = launchedInstances.find(i => i.instance_id === instanceId);
      if (!instance || instance.status !== 'running') {
        stopTerminalReadinessPolling(instanceId);
        return;
      }

      const result = await checkTerminalAvailability(instance);

      if (result.terminal_ready) {
        // Update the instance's terminal_ready status
        setLaunchedInstances(prev => prev.map(inst =>
          inst.instance_id === instanceId
            ? { ...inst, terminal_ready: true }
            : inst
        ));
        stopTerminalReadinessPolling(instanceId);
      }
    };

    // Initial check
    poll();

    // Set up interval
    const intervalId = setInterval(poll, TERMINAL_POLL_INTERVAL_MS);
    terminalReadinessPollingRef.current.set(instanceId, intervalId);
  };

  const stopTerminalReadinessPolling = (instanceId: string) => {
    const intervalId = terminalReadinessPollingRef.current.get(instanceId);
    if (intervalId) {
      clearInterval(intervalId);
      terminalReadinessPollingRef.current.delete(instanceId);
    }
  };

  // Clean up polling on unmount
  useEffect(() => {
    const pollingRef = terminalReadinessPollingRef.current;
    return () => {
      pollingRef.forEach((intervalId) => {
        clearInterval(intervalId);
      });
    };
  }, []);

  // Start polling for running instances that don't have terminal_ready yet
  useEffect(() => {
    launchedInstances.forEach(instance => {
      if (instance.status === 'running' && instance.terminal_ready !== true) {
        startTerminalReadinessPolling(instance.instance_id);
      }
    });
  }, [launchedInstances]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep selected instances in sync when launchedInstances updates (for terminal_ready updates)
  useEffect(() => {
    if (selectedLaunchedInstance) {
      const updated = launchedInstances.find(i => i.instance_id === selectedLaunchedInstance.instance_id);
      if (updated && updated.terminal_ready !== selectedLaunchedInstance.terminal_ready) {
        setSelectedLaunchedInstance(updated);
        // Also update terminalAvailable state for modals
        if (updated.terminal_ready === true) {
          setTerminalAvailable(true);
          setCheckingTerminal(false);
        }
      }
    }
    if (selectedConnection) {
      const updated = launchedInstances.find(i => i.instance_id === selectedConnection.instance_id);
      if (updated && updated.terminal_ready !== selectedConnection.terminal_ready) {
        setSelectedConnection(updated);
      }
    }
  }, [launchedInstances, selectedLaunchedInstance, selectedConnection]);

  const viewInstanceDetails = async (instance: LaunchedInstance) => {
    setSelectedConnection(instance);
    setShowConnectionModal(true);

    // Keep old code for reference
    setSelectedLaunchedInstance(instance);
    // setShowInstanceModal(true);  // Commented out - using connection modal instead

    // Check terminal availability if instance is running
    if (instance.status === 'running') {
      setCheckingTerminal(true);
      setTerminalAvailable(instance.terminal_ready === true);

      // If not ready, the background polling will update it
      if (instance.terminal_ready !== true) {
        startTerminalReadinessPolling(instance.instance_id);
        // Also do an immediate check for the modal
        const result = await checkTerminalAvailability(instance);
        setTerminalAvailable(result.terminal_ready);
        if (result.terminal_ready) {
          setCheckingTerminal(false);
        }
      } else {
        setCheckingTerminal(false);
      }
    } else {
      setTerminalAvailable(false);
      setCheckingTerminal(false);
    }
  };

  const openTerminal = async (instance: LaunchedInstance) => {
    // Check if instance has valid CSP instance_id
    if (!instance || !instance.instance_id) {
      const errorMsg = `Cannot open terminal: Instance has no valid instance_id. Instance data: ${JSON.stringify(instance)}`;
      alert(errorMsg);
      console.error('[openTerminal] Instance missing instance_id:', instance);
      return;
    }
    
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('access_token');

      if (!token) {
        navigate('/login');
        return;
      }

      const provider = (instance.provider || '').toLowerCase();
      let endpoint = `${getApiUrl()}/api/v1/terminal/create`;
      const payload: Record<string, any> = {
        instance_id: instance.instance_id
      };

      if (provider === 'aws') {
        endpoint = `${getApiUrl()}/api/v1/terminal/ssm/create`;
      } else if (provider === 'gcp') {
        endpoint = `${getApiUrl()}/api/v1/terminal/iap/create`;
        const connInfo = instance.connection_info || {};
        payload.project_id = connInfo?.project_id || connInfo?.projectId || connInfo?.project;
        payload.zone = connInfo?.zone || connInfo?.availability_zone || connInfo?.region || instance.region;

        if (payload.zone && typeof payload.zone === 'string' && payload.zone.split('-').length === 2) {
          payload.zone = `${payload.zone}-a`;
        }

        if (!payload.project_id) {
          console.warn('[openTerminal] Missing project_id for GCP terminal request');
        }
        if (!payload.zone) {
          console.warn('[openTerminal] Missing zone for GCP terminal request');
        }
      } else {
        payload.provider = provider || 'aws';
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const data = await response.json();
        const sessionId = data.session_id || data.session?.session_id || data.id;
        if (sessionId) {
          setTerminalSessions(prev => {
            const newMap = new Map(prev);
            const sessionProvider = (instance.provider || 'aws').toLowerCase() as 'aws' | 'gcp' | 'azure';
            newMap.set(sessionId, {
              sessionId: sessionId,
              instanceId: instance.instance_id,
              instanceType: instance.instance_type,
              provider: sessionProvider,
              region: instance.region
            });
            return newMap;
          });

          setShowConnectionModal(false);
          setShowInstanceModal(false);
        } else {
          console.error('[openTerminal] No session ID in response:', data);
          alert('Failed to create terminal session: Invalid response');
        }
      } else {
        const errorData = await response.json();
        console.error('[openTerminal] Request failed with status:', response.status, 'Error:', errorData);
        // Handle error detail that might be an array or string
        const errorMessage = Array.isArray(errorData.detail) 
          ? errorData.detail[0]?.msg || errorData.detail[0] || 'Unknown error'
          : errorData.detail || 'Failed to create terminal session';
        alert(`Failed to create terminal session: ${errorMessage}`);
      }
    } catch (err) {
      console.error('[openTerminal] Exception caught:', err);
      console.error('[openTerminal] Error stack:', (err as Error).stack);
      
      // Provide clear error message based on the error type
      let errorMessage = 'Failed to create terminal session.';
      
      if (err instanceof Error) {
        if (err.message.includes('database ID')) {
          errorMessage = `Invalid instance data: ${err.message}. Please refresh the page and try again.`;
        } else if (err.message.includes('Input should be a valid integer')) {
          errorMessage = 'Invalid instance ID format. The instance data may be corrupted. Please refresh the page.';
        } else {
          errorMessage = `Terminal error: ${err.message}`;
        }
      }
      
      alert(errorMessage);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }

    fetchInstances();
    fetchLaunchedInstances();
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh launched instances if any are launching or terminating
  useEffect(() => {
    const hasAnimatingInstances = launchedInstances.some(inst => {
      const state = getInstanceDisplayState(inst.status, inst.terminal_ready);
      return state === 'launching' || state === 'terminating' || state === 'stopping';
    });

    if (hasAnimatingInstances && viewMode === 'launched') {
      // Set up refresh interval
      const interval = setInterval(() => {
        if (document.hidden) return; // pause while tab is hidden
        fetchLaunchedInstances();
      }, 5000); // Refresh every 5 seconds
      
      setRefreshInterval(interval);
      
      return () => {
        if (interval) clearInterval(interval);
      };
    } else {
      // Clear interval if no launching instances
      if (refreshInterval) {
        clearInterval(refreshInterval);
        setRefreshInterval(null);
      }
    }
  }, [launchedInstances, viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup WebSocket and polling on unmount
  useEffect(() => {
    return () => {
      if (wsServiceRef.current) {
        wsServiceRef.current.close();
        wsServiceRef.current = null;
      }
      if (statusPollingIntervalRef.current) {
        clearInterval(statusPollingIntervalRef.current);
        statusPollingIntervalRef.current = null;
      }
    };
  }, []);

  // Heartbeat: POST every 60s per running instance while tab is visible
  useEffect(() => {
    const runningIds = launchedInstances
      .filter(i => i.status === 'running')
      .map(i => i.instance_id);

    if (runningIds.length === 0) return;

    const sendHeartbeats = () => {
      if (document.hidden) return;
      const token = localStorage.getItem('authToken');
      for (const id of runningIds) {
        fetch(`${getApiUrl()}/api/compute/instances/${id}/heartbeat`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => {});
      }
    };

    sendHeartbeats(); // immediate first beat
    const interval = setInterval(sendHeartbeats, 60_000);
    return () => clearInterval(interval);
  }, [launchedInstances]);

  // Poll idle status every 30s
  useEffect(() => {
    const hasRunning = launchedInstances.some(i => i.status === 'running');
    if (!hasRunning) return;

    const fetchIdleStatus = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const resp = await fetch(`${getApiUrl()}/api/compute/instances/idle-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (resp.ok) {
          const data = await resp.json();
          const map: Record<string, { idle_minutes: number | null; shutdown_deadline: string | null }> = {};
          for (const item of data) {
            map[item.instance_id] = {
              idle_minutes: item.idle_minutes,
              shutdown_deadline: item.shutdown_deadline,
            };
          }
          setIdleStatus(map);
        }
      } catch {
        // non-critical
      }
    };

    fetchIdleStatus();
    const interval = setInterval(fetchIdleStatus, 30_000);
    return () => clearInterval(interval);
  }, [launchedInstances]);


  // Fetch price estimate when instance, disk size, or pricing type changes
  useEffect(() => {
    if (!selectedInstance || !showLaunchModal) {
      setPriceEstimate(null);
      return;
    }
    const token = localStorage.getItem('authToken');
    if (!token) return;

    const controller = new AbortController();
    const params = new URLSearchParams({
      provider: selectedInstance.provider,
      instance_type: selectedInstance.instance_type,
      region: selectedInstance.region,
      spot: String(pricingType === 'spot'),
    });
    if (diskSizeGb) params.set('disk_size_gb', String(diskSizeGb));

    fetch(`${getApiUrl()}/api/compute/price-estimate?${params}`, {
      headers: { 'Authorization': `Bearer ${token}` },
      signal: controller.signal,
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setPriceEstimate(data); })
      .catch(() => {});

    return () => controller.abort();
  }, [selectedInstance, diskSizeGb, pricingType, showLaunchModal]);

  const handleFilterChange = (forceRefresh: boolean = false) => {
    if (forceRefresh) {
      fetchInstancesWithRefresh();
    } else {
      fetchInstances();
    }
  };
  
  const fetchInstancesWithRefresh = async (filters: ComputeFilters = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('authToken');
      if (!token) {
        navigate('/login');
        return;
      }

      // Use semantic GPU API with cache bypass for fresh data
      const response = await fetch(`${getApiUrl()}/api/gpu/discover?bypass_cache=true`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch instances: ${response.status}`);
      }

      const data = await response.json();
      // Response is a raw array (same as /api/gpu/discover) — map to expected format
      const mappedInstances = data.map((gpu: any) => ({
        instance_type: gpu.provider_instance_id,
        region: gpu.region,
        gpu_type: gpu.gpu_model_name,
        gpu_count: gpu.gpu_count,
        pricing: {
          on_demand: gpu.on_demand_price_per_hour,
          spot: gpu.spot_price_per_hour
        },
        availability_zones: gpu.availability_zones,
        provider: gpu.provider,
        vcpus: gpu.vcpus,
        memory_gb: gpu.system_memory_gib,
        storage_gb: gpu.storage_size_gb,
        network_bandwidth_gbps: gpu.network_bandwidth_gbps,
        last_updated: gpu.pricing?.last_updated || gpu.specs_last_updated
      }));
      setInstances(mappedInstances);

    } catch (err: any) {
      console.error('Error fetching instances:', err);
      setError(err.message || 'Failed to fetch instances');
      setInstances([]);
    } finally {
      setLoading(false);
    }
  };

  const getProviderIcon = (provider: string) => {
    const className = "w-5 h-5";
    
    switch (provider.toLowerCase()) {
      case 'aws':
        return (
          <img 
            src="/icons/icons8-aws-48.svg" 
            alt="AWS" 
            className={className}
          />
        );
      case 'gcp':
        return (
          <img 
            src="/icons/icons8-google-cloud-48.svg" 
            alt="Google Cloud" 
            className={className}
          />
        );
      case 'azure':
        return (
          <img
            src="/icons/azure.svg"
            alt="Azure"
            className={className}
          />
        );
      default:
        return (
          <svg className={className} viewBox="0 0 24 24" fill="none">
            <path
              d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"
              fill="#6366F1"
            />
          </svg>
        );
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'available':
        return 'text-green-500 bg-green-500/20';
      case 'limited':
        return 'text-yellow-500 bg-yellow-500/20';
      case 'unavailable':
        return 'text-red-500 bg-red-500/20';
      default:
        return 'text-gray-500 bg-gray-500/20';
    }
  };

  const formatPrice = (price?: number) => {
    if (!price) return 'N/A';
    return `$${price.toFixed(3)}`;
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedInstances = useMemo(() => {
    if (!instances.length) return [];
    
    return [...instances].sort((a, b) => {
      let aVal: any = a[sortField];
      let bVal: any = b[sortField];
      
      // Handle null/undefined values
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return sortDirection === 'asc' ? 1 : -1;
      if (bVal == null) return sortDirection === 'asc' ? -1 : 1;
      
      // Convert to comparable types
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [instances, sortField, sortDirection]);

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <span className="text-gray-500">⇅</span>;
    }
    return sortDirection === 'asc' ? <span className="text-blue-400">↑</span> : <span className="text-blue-400">↓</span>;
  };

  // Connection Modal Component
  const ConnectionModal = () => {
    if (!showConnectionModal || !selectedConnection) {
      return null;
    }

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-gray-900 rounded-xl border border-gray-700 p-6 max-w-md w-full mx-4">
          <h2 className="text-xl font-bold text-white mb-4">Connect to Instance</h2>
          
          <div className="bg-gray-800/50 rounded-lg p-4 mb-4">
            <div className="text-sm text-gray-400 mb-1">Instance</div>
            <div className="text-white font-medium">{selectedConnection.instance_type}</div>
            <div className="text-xs text-gray-500 mt-1">
              {selectedConnection.provider.toUpperCase()} • {selectedConnection.region}
            </div>
          </div>
          
          {/* Error display for failed/terminated instances */}
          {(selectedConnection.user_error_explanation || selectedConnection.error_message) && (
            <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 mb-4">
              <div className="text-sm font-medium text-red-300 mb-1">Launch Failed</div>
              <p className="text-sm text-red-200">
                {selectedConnection.user_error_explanation || selectedConnection.error_message}
              </p>
              {selectedConnection.user_error_explanation && selectedConnection.error_message && (
                <details className="mt-2">
                  <summary className="text-xs text-red-400/70 cursor-pointer hover:text-red-300">Technical details</summary>
                  <pre className="mt-1 text-xs text-red-400/50 whitespace-pre-wrap break-all font-mono">
                    {selectedConnection.error_message}
                  </pre>
                </details>
              )}
            </div>
          )}

          <div className="space-y-3">
            {/* Terminal button - shows status when not ready */}
            {selectedConnection.status === 'running' && selectedConnection.terminal_ready === true ? (
              <button
                onClick={() => {
                  try {
                    openTerminal(selectedConnection);
                  } catch (error) {
                    console.error('[ConnectionModal] Error calling openTerminal:', error);
                  }
                }}
                className="w-full p-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center justify-between transition-colors"
              >
                <span className="flex flex-col text-left">
                  <span>Open Terminal</span>
                  {selectedConnection.connection_info?.terminal_access && (
                    <span className="text-xs text-blue-200/90">
                      {`via ${selectedConnection.connection_info.terminal_access}`}
                    </span>
                  )}
                </span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </button>
            ) : selectedConnection.status === 'running' ? (
              <div className="w-full p-3 bg-gray-700 text-gray-300 rounded-lg flex items-center justify-between">
                <span className="flex flex-col text-left">
                  <span className="flex items-center space-x-2">
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Terminal Starting...</span>
                  </span>
                  <span className="text-xs text-gray-400 mt-1">
                    {selectedConnection.provider === 'aws' ? 'SSM agent initializing...' : 'IAP tunnel initializing...'}
                  </span>
                </span>
              </div>
            ) : (
              <div className="w-full p-3 bg-gray-700 text-gray-400 rounded-lg">
                <span>Instance is {selectedConnection.status}</span>
              </div>
            )}
            
            {/* Jupyter notebook button - gated on jupyter_ready */}
            {selectedConnection.status === 'running' && selectedConnection.jupyter_ready === true ? (
              <button
                onClick={() => {
                  sessionStorage.setItem('notebook_launch_params', JSON.stringify({
                    instance_id: selectedConnection.instance_id,
                  }));
                  navigate('/dashboard/notebook/new-session');
                  setShowConnectionModal(false);
                }}
                className="w-full p-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center justify-between transition-colors"
              >
                <span className="flex flex-col text-left">
                  <span>Open Notebook</span>
                  <span className="text-xs text-purple-200/90">Jupyter environment ready</span>
                </span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </button>
            ) : selectedConnection.status === 'running' ? (
              <div className="w-full p-3 bg-gray-700 text-gray-300 rounded-lg flex items-center justify-between">
                <span className="flex flex-col text-left">
                  <span className="flex items-center space-x-2">
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Notebook Starting...</span>
                  </span>
                  <span className="text-xs text-gray-400 mt-1">Jupyter environment initializing...</span>
                </span>
              </div>
            ) : null}
            
            {selectedConnection.connection_info?.ssh_command && (
              <div className="p-3 bg-gray-800 rounded-lg">
                <div className="text-xs text-gray-400 mb-2">SSH Command</div>
                <code className="text-xs text-green-400 break-all">
                  {selectedConnection.connection_info.ssh_command}
                </code>
              </div>
            )}
            
            <div className="border-t border-gray-700 pt-3">
              <button
                onClick={() => setShowConnectionModal(false)}
                className="w-full p-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading && instances.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-gray-400">Loading real-time GPU pricing data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
          <style>{`
            @keyframes progress {
              0% {
                transform: translateX(-100%);
              }
              100% {
                transform: translateX(100%);
              }
            }
            @keyframes fadeIn {
              from {
                opacity: 0;
                transform: translateY(10px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            @keyframes slideIn {
              from {
                opacity: 0;
                transform: translateX(-20px);
              }
              to {
                opacity: 1;
                transform: translateX(0);
              }
            }
            .animate-fadeIn {
              animation: fadeIn 0.3s ease-out;
            }
            .animate-slideIn {
              animation: slideIn 0.3s ease-out;
            }
          `}</style>

          {/* Launch failure toast */}
          {failureToast && (
            <div className="fixed top-4 right-4 z-50 max-w-md animate-fadeIn">
              <div className="bg-red-900/95 border border-red-700 rounded-lg p-4 shadow-xl">
                <div className="flex items-start space-x-3">
                  <svg className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-red-200">Launch Failed</h4>
                    <p className="text-sm text-red-300/90 mt-1">{failureToast.message}</p>
                  </div>
                  <button onClick={() => setFailureToast(null)} className="text-red-400 hover:text-red-300">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-white mb-2">GPU Compute</h1>
              <p className="text-gray-400">
                Real-time GPU pricing and availability from cloud providers
              </p>
            </div>

            {/* View Toggle */}
            <div className="flex justify-between items-center mb-6">
              <div className="flex bg-gray-800 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('semantic')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    viewMode === 'semantic'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Browse GPUs
                </button>
                <button
                  onClick={() => setViewMode('launched')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    viewMode === 'launched'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  My Instances ({launchedInstances.length})
                </button>
                {/* Comparison tab hidden until backend endpoint exists */}
              </div>

              <div className="flex items-center gap-2">
                {/* Data freshness indicator */}
                {catalogHealth && (
                  <div className="flex items-center" title={`Catalog Health: ${catalogHealth.fresh_count} fresh, ${catalogHealth.stale_count} stale, ${catalogHealth.very_stale_count} very stale`}>
                    <div className={`w-2 h-2 rounded-full mr-2 ${
                      catalogHealth.very_stale_count > 0 ? 'bg-red-500' :
                      catalogHealth.stale_count > 0 ? 'bg-yellow-500' : 'bg-green-500'
                    }`}></div>
                    <span className="text-xs text-gray-400">
                      {catalogHealth.very_stale_count > 0 ? 'Data needs update' :
                       catalogHealth.stale_count > 0 ? 'Data partially stale' : 'Data fresh'}
                    </span>
                  </div>
                )}
                {lastRefresh && (
                  <span className="text-xs text-gray-500">
                    Updated {getDataFreshness(lastRefresh.toISOString()).text}
                  </span>
                )}
                <button
                  onClick={() => handleFilterChange(true)}
                  disabled={loading}
                  className="p-2 rounded-lg font-medium transition-colors bg-gray-800 hover:bg-gray-700 text-white border border-gray-600"
                  title="Refresh"
                >
                  <svg 
                    className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              </div>
            </div>


            {error && (
              <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-400">Error loading data</h3>
                    <p className="mt-1 text-sm text-red-300">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Legacy Instance View - Removed */}
            {false && (
              <>
                {loading && (
                  <div className="text-center py-4 mb-6">
                    <div className="inline-flex items-center px-4 py-2 bg-blue-600/20 rounded-lg">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-400 mr-2"></div>
                      <span className="text-blue-400">Fetching latest pricing...</span>
                    </div>
                  </div>
                )}

                {instances.length > 0 ? (
                  <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-[800px]">
                        <thead className="bg-gray-800/50">
                          <tr className="text-left">
                            <th className="px-3 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider min-w-[100px]">
                              <button 
                                onClick={() => handleSort('provider')}
                                className="flex items-center space-x-1 hover:text-white transition-colors"
                              >
                                <span>Provider</span>
                                {getSortIcon('provider')}
                              </button>
                            </th>
                            <th className="px-3 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider min-w-[120px]">
                              <button 
                                onClick={() => handleSort('gpu_type')}
                                className="flex items-center space-x-1 hover:text-white transition-colors"
                              >
                                <span>GPU</span>
                                {getSortIcon('gpu_type')}
                              </button>
                            </th>
                            <th className="px-3 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider min-w-[140px]">
                              <button 
                                onClick={() => handleSort('instance_type')}
                                className="flex items-center space-x-1 hover:text-white transition-colors"
                              >
                                <span>Instance</span>
                                {getSortIcon('instance_type')}
                              </button>
                            </th>
                            <th className="px-3 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider min-w-[80px] hidden sm:table-cell">
                              <button 
                                onClick={() => handleSort('vcpus')}
                                className="flex items-center space-x-1 hover:text-white transition-colors"
                              >
                                <span>vCPUs</span>
                                {getSortIcon('vcpus')}
                              </button>
                            </th>
                            <th className="px-3 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider min-w-[80px] hidden md:table-cell">
                              <button 
                                onClick={() => handleSort('memory_gb')}
                                className="flex items-center space-x-1 hover:text-white transition-colors"
                              >
                                <span>Memory</span>
                                {getSortIcon('memory_gb')}
                              </button>
                            </th>
                            <th className="px-3 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider min-w-[100px]">
                              <button 
                                onClick={() => handleSort('price_per_hour_ondemand')}
                                className="flex items-center space-x-1 hover:text-white transition-colors"
                              >
                                <span>Price</span>
                                {getSortIcon('price_per_hour_ondemand')}
                              </button>
                            </th>
                            <th className="px-3 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider min-w-[80px] hidden lg:table-cell">
                              <button 
                                onClick={() => handleSort('price_per_hour_spot')}
                                className="flex items-center space-x-1 hover:text-white transition-colors"
                              >
                                <span>Spot</span>
                                {getSortIcon('price_per_hour_spot')}
                              </button>
                            </th>
                            <th className="px-3 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider min-w-[80px] hidden xl:table-cell">
                              <button 
                                onClick={() => handleSort('price_per_gpu_hour')}
                                className="flex items-center space-x-1 hover:text-white transition-colors"
                              >
                                <span>Per GPU</span>
                                {getSortIcon('price_per_gpu_hour')}
                              </button>
                            </th>
                            <th className="px-3 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider min-w-[80px] hidden lg:table-cell">Status</th>
                            <th className="px-3 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider min-w-[80px]">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800">
                          {sortedInstances.map((instance, index) => (
                            <tr key={`${instance.provider}-${instance.instance_type}-${index}`} 
                                className="hover:bg-gray-800/30 transition-colors">
                              <td className="px-3 py-3">
                                <div className="flex items-center">
                                  <span className="text-lg mr-2">{getProviderIcon(instance.provider)}</span>
                                  <span className="text-sm font-medium text-white">{instance.provider.toUpperCase()}</span>
                                </div>
                              </td>
                              <td className="px-3 py-3">
                                <div className="text-sm font-medium text-white">
                                  {instance.gpu_type} ({instance.gpu_count}x)
                                </div>
                                <div className="text-xs text-gray-400">{instance.gpu_memory_gb}GB each</div>
                              </td>
                              <td className="px-3 py-3">
                                <div className="text-sm font-medium text-white">{instance.instance_type}</div>
                                <div className="text-xs text-gray-400">{instance.region}</div>
                              </td>
                              <td className="px-3 py-3 text-sm text-gray-300 hidden sm:table-cell">{instance.vcpus}</td>
                              <td className="px-3 py-3 text-sm text-gray-300 hidden md:table-cell">{instance.memory_gb}GB</td>
                              <td className="px-3 py-3">
                                <div className="text-sm font-bold text-white">
                                  {formatPrice(instance.price_per_hour_ondemand)}/hr
                                </div>
                                <div className="text-xs text-gray-400 sm:hidden">
                                  {instance.vcpus} vCPUs • {instance.memory_gb}GB
                                </div>
                              </td>
                              <td className="px-3 py-3 hidden lg:table-cell">
                                {instance.price_per_hour_spot ? (
                                  <span className="text-sm text-green-400">
                                    {formatPrice(instance.price_per_hour_spot)}/hr
                                  </span>
                                ) : (
                                  <span className="text-xs text-gray-500">N/A</span>
                                )}
                              </td>
                              <td className="px-3 py-3 hidden xl:table-cell">
                                {instance.price_per_gpu_hour ? (
                                  <span className="text-sm text-gray-300">
                                    {formatPrice(instance.price_per_gpu_hour)}/hr
                                  </span>
                                ) : (
                                  <span className="text-xs text-gray-500">N/A</span>
                                )}
                              </td>
                              <td className="px-3 py-3 hidden lg:table-cell">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(instance.status)}`}>
                                  {instance.status}
                                </span>
                              </td>
                              <td className="px-3 py-3">
                                <button
                                  onClick={() => handleLaunch(instance)}
                                  disabled={instance.status.toLowerCase() !== 'available'}
                                  className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                                    instance.status.toLowerCase() === 'available'
                                      ? 'bg-white text-black hover:bg-gray-100'
                                      : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                                  }`}
                                >
                                  {instance.status.toLowerCase() === 'available' ? 'Launch' : 'N/A'}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  !loading && (
                    <div className="text-center py-12 bg-gray-900 rounded-xl border border-gray-800">
                      <div className="w-16 h-16 bg-gray-800/50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-medium text-white mb-2">No instances found</h3>
                      <p className="text-gray-400">
                        Try adjusting your filters to see available GPU instances
                      </p>
                    </div>
                  )
                )}
              </>
            )}

            {/* Semantic GPU View */}
            {viewMode === 'semantic' && (
              <SemanticGPUBrowse
                onLaunchGPU={(gpu) => {
                  // Convert semantic GPU to legacy format for launch modal
                  const legacyInstance: GPUInstance = {
                    provider: gpu.provider,
                    region: gpu.region,
                    instance_type: gpu.provider_instance_id,
                    instance_name: gpu.gpu_model_name || 'GPU Instance',
                    vcpus: gpu.vcpus || 0,
                    memory_gb: gpu.system_memory_gib || 0,
                    gpu_count: gpu.gpu_count || 1,
                    gpu_type: gpu.gpu_model_name || 'Unknown',
                    gpu_memory_gb: gpu.gpu_memory_per_device_gb || 0,
                    price_per_hour_ondemand: gpu.on_demand_price_per_hour || undefined,
                    price_per_hour_spot: gpu.spot_price_per_hour || undefined,
                    status: 'available',
                    use_cases: gpu.recommended_workloads || []
                  };
                  setSelectedInstance(legacyInstance);
                  setShowLaunchModal(true);
                }}
                onViewDetails={(gpu) => {
                  setSelectedGPUDetails(gpu);
                  setShowGPUDetailsModal(true);
                }}
              />
            )}

            {/* Launched Instances View */}
            {viewMode === 'launched' && (
              <div className="space-y-6">
                {/* Header with refresh button */}
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-semibold text-white">Your Instances</h2>
                  <div className="flex items-center space-x-3">
                    {refreshMessage && (
                      <span className={`text-sm animate-fade-in ${
                        refreshMessage.startsWith('✓') ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {refreshMessage}
                      </span>
                    )}
                    <button
                      onClick={() => fetchLaunchedInstances(true)}
                      disabled={refreshingLaunched}
                      className="p-2 rounded-lg font-medium transition-colors bg-gray-800 hover:bg-gray-700 text-white border border-gray-600"
                      title="Refresh"
                    >
                      <svg 
                        className={`w-5 h-5 ${refreshingLaunched ? 'animate-spin' : ''}`} 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Sync Status Warning Banner */}
                {syncStatus && (
                  <>
                    {!syncStatus.aws.success && (
                      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                        <div className="flex items-start">
                          <svg className="w-5 h-5 text-yellow-400 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          <div className="flex-1">
                            <h3 className="text-sm font-medium text-yellow-400">AWS Sync Warning</h3>
                            <p className="text-sm text-gray-300 mt-1">
                              Unable to sync AWS instance status. Displayed data may be out of date.
                              {syncStatus.aws.error && <span className="block text-xs text-gray-400 mt-1">Error: {syncStatus.aws.error}</span>}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    {!syncStatus.gcp.success && syncStatus.gcp.error && (
                      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                        <div className="flex items-start">
                          <svg className="w-5 h-5 text-yellow-400 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          <div className="flex-1">
                            <h3 className="text-sm font-medium text-yellow-400">GCP Sync Warning</h3>
                            <p className="text-sm text-gray-300 mt-1">
                              Unable to sync GCP instance status. Displayed data may be out of date.
                              <span className="block text-xs text-gray-400 mt-1">Error: {syncStatus.gcp.error}</span>
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {balanceWarning && (
                  <div className="bg-yellow-900/30 border border-yellow-700/50 rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-yellow-300 font-medium text-sm">Low Balance Warning</p>
                        <p className="text-yellow-200/70 text-xs mt-1">
                          Balance: ${balanceWarning.balance.toFixed(2)} — Estimated {balanceWarning.estimated_minutes} min remaining.
                          {' '}Add funds to prevent automatic instance termination.
                        </p>
                      </div>
                      <button
                        onClick={dismissBalanceWarning}
                        className="text-yellow-400 hover:text-yellow-200 ml-4"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                )}

                <IdleWarningBanner
                  warnings={idleWarnings}
                  onDismiss={dismissWarning}
                />

                {launchedInstances.length > 0 ? (
                  <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-800/50">
                          <tr className="text-left">
                            <th className="px-4 py-3 text-sm font-medium text-gray-300">Provider</th>
                            <th className="px-4 py-3 text-sm font-medium text-gray-300">Instance</th>
                            <th className="px-4 py-3 text-sm font-medium text-gray-300">Status</th>
                            <th className="px-4 py-3 text-sm font-medium text-gray-300">Runtime</th>
                            <th className="px-4 py-3 text-sm font-medium text-gray-300">Cost</th>
                            <th className="px-4 py-3 text-sm font-medium text-gray-300">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800">
                          {launchedInstances.map((instance) => (
                            <React.Fragment key={instance.instance_id}>
                            <tr className="hover:bg-gray-800/30">
                              <td className="px-4 py-3">
                                <div className="flex items-center">
                                  <span className="text-lg mr-2">{getProviderIcon(instance.provider)}</span>
                                  <span className="text-sm font-medium text-white">{instance.provider.toUpperCase()}</span>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="text-sm font-medium text-white">{instance.instance_type}</div>
                                <div className="text-xs text-gray-400">{instance.region}</div>
                                {instance.project_name && (
                                  <div className="text-xs text-blue-400">{instance.project_name}</div>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex flex-col space-y-2 min-w-[200px]">
                                  {(() => {
                                    const displayState = getInstanceDisplayState(instance.status, instance.terminal_ready);
                                    const visuals = getStatusVisuals(displayState);

                                    if (displayState === 'launching') return (
                                    <div className="w-full">
                                      <div className="relative w-full h-8 bg-gray-800 rounded-full overflow-hidden">
                                        <div className="absolute inset-0 flex items-center justify-center z-10">
                                          <span className="text-xs font-semibold text-white">
                                            Launching...
                                          </span>
                                        </div>
                                        <div className="absolute inset-0 overflow-hidden">
                                          <div className="h-full w-full bg-gradient-to-r from-transparent via-yellow-500/40 to-transparent"
                                               style={{
                                                 animation: 'progress 2s ease-in-out infinite',
                                                 transform: 'translateX(-100%)'
                                               }}>
                                          </div>
                                        </div>
                                      </div>
                                      {instance.connection_info?.status_message && (
                                        <span className="text-xs text-gray-400 text-center block mt-1">
                                          {instance.connection_info.status_message}
                                        </span>
                                      )}
                                    </div>
                                    );

                                    if (displayState === 'starting') return (
                                    <div className="w-full">
                                      <div className="relative w-full h-8 bg-gray-800 rounded-full overflow-hidden">
                                        <div className="absolute inset-0 flex items-center justify-center z-10">
                                          <span className="text-xs font-semibold text-white">
                                            Starting...
                                          </span>
                                        </div>
                                        <div className="absolute inset-0 overflow-hidden">
                                          <div className="h-full w-full bg-gradient-to-r from-transparent via-blue-500/40 to-transparent"
                                               style={{
                                                 animation: 'progress 2s ease-in-out infinite',
                                                 transform: 'translateX(-100%)'
                                               }}>
                                          </div>
                                        </div>
                                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500/30">
                                          <div className="h-full bg-blue-500 animate-pulse" style={{ width: '60%' }}></div>
                                        </div>
                                      </div>
                                      <span className="text-xs text-blue-400 text-center block mt-1">
                                        {instance.provider === 'aws' ? 'SSM agent initializing...' : 'IAP tunnel initializing...'}
                                      </span>
                                    </div>
                                    );

                                    if (displayState === 'running') return (
                                    <div className="w-full">
                                      <div className="relative w-full h-8 bg-green-500/20 rounded-full overflow-hidden">
                                        <div className="absolute inset-0 flex items-center justify-center">
                                          <span className="text-xs font-semibold text-green-400">
                                            Running
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                    );

                                    if (displayState === 'terminating') return (
                                    <div className="w-full">
                                      <div className="relative w-full h-8 bg-gray-800 rounded-full overflow-hidden">
                                        <div className="absolute inset-0 flex items-center justify-center z-10">
                                          <span className="text-xs font-semibold text-white">
                                            Terminating...
                                          </span>
                                        </div>
                                        <div className="absolute inset-0 overflow-hidden">
                                          <div className="h-full w-full bg-gradient-to-r from-transparent via-red-500/40 to-transparent"
                                               style={{
                                                 animation: 'progress 2s ease-in-out infinite',
                                                 transform: 'translateX(-100%)'
                                               }}>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    );

                                    return (
                                    <div className="w-full">
                                      <div className="relative w-full h-8 flex items-center justify-center">
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${visuals.badgeClasses}`}>
                                          {visuals.label}
                                        </span>
                                      </div>
                                    </div>
                                    );
                                  })()}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                {(() => {
                                  // Live runtime for running instances
                                  const liveRuntime = instance.status === 'running' && instance.launch_time
                                    ? (Date.now() - new Date(instance.launch_time).getTime()) / (1000 * 60 * 60)
                                    : instance.runtime_hours || 0;
                                  return (
                                    <>
                                      <div className="text-sm text-white">
                                        {liveRuntime > 0 ? `${liveRuntime.toFixed(2)}h` : '0h'}
                                      </div>
                                      <div className="text-xs text-gray-400">
                                        {instance.launch_time ? new Date(instance.launch_time).toLocaleString() : 'N/A'}
                                      </div>
                                    </>
                                  );
                                })()}
                                {instance.status === 'running' && idleStatus[instance.instance_id]?.idle_minutes != null && (
                                  <div className={`text-xs mt-1 ${
                                    (idleStatus[instance.instance_id].idle_minutes ?? 0) >= 10
                                      ? 'text-amber-400'
                                      : 'text-gray-500'
                                  }`}>
                                    Idle {Math.floor(idleStatus[instance.instance_id].idle_minutes ?? 0)}m
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                {(() => {
                                  // Live cost for running instances
                                  if (instance.status === 'running' && instance.launch_time && instance.hourly_cost) {
                                    const hours = (Date.now() - new Date(instance.launch_time).getTime()) / (1000 * 60 * 60);
                                    const timeCost = hours * instance.hourly_cost;
                                    const minCharge = instance.hourly_cost * (10 / 60);
                                    const displayCost = Math.max(timeCost, minCharge);
                                    return (
                                      <>
                                        <div className="text-sm font-bold text-white">
                                          ${displayCost.toFixed(2)}
                                          {timeCost < minCharge && (
                                            <span className="text-[10px] font-normal text-gray-500 ml-1">(min)</span>
                                          )}
                                        </div>
                                        <div className="text-xs text-gray-400">
                                          ${instance.hourly_cost.toFixed(3)}/hr
                                        </div>
                                      </>
                                    );
                                  }
                                  return (
                                    <>
                                      <div className="text-sm font-bold text-white">
                                        ${instance.total_cost?.toFixed(2) || '0.00'}
                                      </div>
                                      {instance.hourly_cost && (
                                        <div className="text-xs text-gray-400">
                                          ${instance.hourly_cost.toFixed(3)}/hr
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex space-x-2">
                                  {/* Connect button - enabled only when terminal is ready */}
                                  {instance.status === 'running' && instance.terminal_ready === true ? (
                                    <button
                                      onClick={() => viewInstanceDetails(instance)}
                                      className="px-3 py-1 bg-white hover:bg-gray-100 text-black rounded text-xs font-medium transition-colors"
                                    >
                                      Connect
                                    </button>
                                  ) : instance.status === 'running' ? (
                                    <button
                                      disabled
                                      className="px-3 py-1 bg-gray-700 text-gray-400 rounded text-xs font-medium cursor-not-allowed flex items-center space-x-1"
                                    >
                                      <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                      </svg>
                                      <span>Starting...</span>
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => viewInstanceDetails(instance)}
                                      className="px-3 py-1 bg-gray-700 text-gray-300 rounded text-xs font-medium transition-colors hover:bg-gray-600"
                                    >
                                      Details
                                    </button>
                                  )}
                                  {['running', 'launching', 'LAUNCHING'].includes(instance.status) && (
                                    <button
                                      onClick={() => terminateInstance(instance.instance_id)}
                                      className="px-3 py-1 bg-gray-800 hover:bg-red-600 text-white rounded text-xs font-medium transition-colors border border-gray-600"
                                    >
                                      Terminate
                                    </button>
                                  )}
                                  {['terminated', 'stopped', 'error', 'launch_failed'].includes(instance.status) && (
                                    <button
                                      onClick={() => {
                                        // Use instance_id if available, otherwise use database id for failed launches
                                        removeInstance(instance.instance_id);
                                      }}
                                      className="px-3 py-1 bg-gray-800 hover:bg-gray-700 text-white rounded text-xs font-medium transition-colors border border-gray-600"
                                    >
                                      Remove
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                            {/* Error explanation row for failed instances */}
                            {instance.status === 'error' && (instance.user_error_explanation || instance.error_message) && (
                              <tr className="bg-red-950/30 border-b border-red-900/30">
                                <td colSpan={7} className="px-4 py-3">
                                  <div className="flex items-start space-x-3">
                                    <svg className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                    </svg>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm text-red-200">
                                        {instance.user_error_explanation || instance.error_message}
                                      </p>
                                      {instance.user_error_explanation && instance.error_message && (
                                        <details className="mt-2">
                                          <summary className="text-xs text-red-400/70 cursor-pointer hover:text-red-300">
                                            Technical details
                                          </summary>
                                          <pre className="mt-1 text-xs text-red-400/50 whitespace-pre-wrap break-all font-mono">
                                            {instance.error_message}
                                          </pre>
                                        </details>
                                      )}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 bg-gray-900 rounded-xl border border-gray-800">
                    <div className="w-16 h-16 bg-gray-800/50 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-white mb-2">No instances launched</h3>
                    <p className="text-gray-400 mb-4">
                      Launch your first GPU instance to get started
                    </p>
                    <button
                      onClick={() => setViewMode('semantic')}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                    >
                      Browse GPUs
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Launch Modal */}
            {showLaunchModal && selectedInstance && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <Draggable 
                  handle=".drag-handle" 
                  bounds="parent"
                  defaultPosition={{x: 0, y: 0}}
                >
                  <div className="bg-gray-900 rounded-xl border border-gray-700 shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
                    {/* Drag Handle Bar */}
                    <div className="drag-handle bg-gray-800 rounded-t-xl px-6 py-3 flex items-center justify-between cursor-move border-b border-gray-700">
                      <h3 className="text-sm font-semibold text-gray-100 select-none">
                        {launching ? 'Launching Instance...' : 'Launch GPU Instance'}
                      </h3>
                      <button
                        onClick={() => {
                          setShowLaunchModal(false);
                          if (launching) {
                            setShowProgressNotification(true);
                          }
                        }}
                        className="text-gray-400 hover:text-gray-100 transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    
                    <div className="p-6 overflow-y-auto flex-1 min-h-0">
                      {/* Instance Details - Hidden when launching */}
                      {!launching && (
                        <div className="bg-gray-800 rounded-lg p-4 mb-6 animate-fadeIn border border-gray-700">
                    <div className="grid grid-cols-2 gap-4 text-center">
                      <div>
                        <span className="text-gray-400 text-sm block mb-2">Provider</span>
                        <div className="flex items-center justify-center h-6">
                          <span className="inline-block w-5 h-5 mr-2">{getProviderIcon(selectedInstance.provider)}</span>
                          <span className="text-gray-100 font-medium">{selectedInstance.provider.toUpperCase()}</span>
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-400 text-sm block mb-2">Instance Type</span>
                        <div className="text-gray-100 font-medium h-6 flex items-center justify-center">{selectedInstance.instance_type}</div>
                      </div>
                      <div>
                        <span className="text-gray-400 text-sm block mb-2">GPU</span>
                        <div className="text-gray-100 font-medium h-6 flex items-center justify-center">
                          {selectedInstance.gpu_type} ({selectedInstance.gpu_count}x)
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-400 text-sm block mb-2">Price</span>
                        {priceEstimate ? (
                          <>
                            <div className="text-gray-100 font-bold h-6 flex items-center justify-center">
                              {formatPrice(priceEstimate.total_price_per_hour)}/hr
                            </div>
                            <div className="text-gray-400 text-xs mt-1 text-center">
                              Compute {formatPrice(priceEstimate.compute_price_per_hour)} + Disk {formatPrice(priceEstimate.disk_price_per_hour)}
                            </div>
                          </>
                        ) : (
                          <div className="text-gray-100 font-bold h-6 flex items-center justify-center">
                            {pricingType === 'spot' && selectedInstance.price_per_hour_spot
                              ? `${formatPrice(selectedInstance.price_per_hour_spot)}/hr`
                              : `${formatPrice(selectedInstance.price_per_hour_ondemand)}/hr`
                            }
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                      )}

                      {/* Launch Options - Hidden when launching */}
                  <div className="space-y-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">
                        Project Name (Optional)
                      </label>
                      <input
                        type="text"
                        value={projectName}
                        onChange={(e) => setProjectName(e.target.value)}
                        className="w-full bg-gray-800 border border-gray-700 text-gray-100 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500"
                        placeholder="e.g. ML Training, Data Analysis"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-3">
                        Pricing Type
                      </label>
                      <div className="space-y-2">
                        <label className="flex items-start p-3 bg-gray-800 border border-gray-700 rounded-lg cursor-pointer hover:border-blue-600 transition-colors">
                          <input
                            type="radio"
                            name="pricingType"
                            value="ondemand"
                            checked={pricingType === 'ondemand'}
                            onChange={(e) => setPricingType(e.target.value as 'ondemand' | 'spot')}
                            className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-600 focus:ring-blue-500 mt-0.5"
                          />
                          <div className="ml-3 flex-1">
                            <div className="text-gray-100 font-medium">On-Demand</div>
                            <div className="text-sm text-gray-400 mt-1">
                              <span className="font-mono">{pricingType === 'ondemand' && priceEstimate ? formatPrice(priceEstimate.total_price_per_hour) : formatPrice(selectedInstance.price_per_hour_ondemand)}/hr</span>
                              <span className="mx-2">•</span>
                              <span>Guaranteed availability</span>
                            </div>
                          </div>
                        </label>
                        
                        {selectedInstance.price_per_hour_spot ? (
                          <label className="flex items-start p-3 bg-gray-800 border border-gray-700 rounded-lg cursor-pointer hover:border-blue-600 transition-colors">
                            <input
                              type="radio"
                              name="pricingType"
                              value="spot"
                              checked={pricingType === 'spot'}
                              onChange={(e) => setPricingType(e.target.value as 'ondemand' | 'spot')}
                              className="w-4 h-4 text-blue-600 bg-gray-800 border-gray-600 focus:ring-blue-500 mt-0.5"
                            />
                            <div className="ml-3 flex-1">
                              <div className="text-gray-100 font-medium">Spot Instance</div>
                              <div className="text-sm text-gray-400 mt-1">
                                <span className="font-mono">{pricingType === 'spot' && priceEstimate ? formatPrice(priceEstimate.total_price_per_hour) : formatPrice(selectedInstance.price_per_hour_spot)}/hr</span>
                                <span className="mx-2">•</span>
                                <span className="text-green-400">{selectedInstance.price_per_hour_ondemand && selectedInstance.price_per_hour_ondemand > 0 ? `${Math.round((1 - (selectedInstance.price_per_hour_spot ?? 0) / selectedInstance.price_per_hour_ondemand) * 100)}% savings` : 'Spot pricing'}</span>
                                <span className="mx-2">•</span>
                                <span className="text-yellow-400">May be interrupted</span>
                              </div>
                            </div>
                          </label>
                        ) : (
                          <div className="p-3 bg-gray-800/50 border border-gray-700 rounded-lg">
                            <div className="text-sm text-gray-500 flex items-center">
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              Spot pricing not available for this instance type
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Disk Size */}
                  <div className="mb-6">
                    <label className="text-sm text-gray-400 block mb-2">Disk Size</label>
                    <select
                      value={diskSizeGb ?? ''}
                      onChange={(e) => setDiskSizeGb(e.target.value ? Number(e.target.value) : null)}
                      className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
                    >
                      <option value="">Auto (recommended)</option>
                      <option value="100">100 GB</option>
                      <option value="150">150 GB</option>
                      <option value="200">200 GB</option>
                      <option value="500">500 GB</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {priceEstimate
                        ? `${priceEstimate.disk_size_gb} GB ${priceEstimate.disk_type} — ${formatPrice(priceEstimate.disk_price_per_hour)}/hr ($${(priceEstimate.disk_price_per_hour * 730).toFixed(2)}/mo)`
                        : 'Auto sizes disk based on instance tier (100-200 GB)'
                      }
                    </p>
                  </div>

                  {/* Cost Breakdown */}
                  {priceEstimate && !launching && (
                    <div className="bg-gray-800/60 border border-gray-700 rounded-lg p-4 mb-6">
                      <div className="text-sm text-gray-400 mb-2 font-medium">Estimated Cost</div>
                      <div className="space-y-1.5">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Compute ({pricingType === 'spot' ? 'Spot' : 'On-Demand'})</span>
                          <span className="text-gray-200 font-mono">{formatPrice(priceEstimate.compute_price_per_hour)}/hr</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Disk ({priceEstimate.disk_size_gb} GB {priceEstimate.disk_type})</span>
                          <span className="text-gray-200 font-mono">{formatPrice(priceEstimate.disk_price_per_hour)}/hr</span>
                        </div>
                        <div className="border-t border-gray-700 pt-1.5 flex justify-between text-sm font-medium">
                          <span className="text-gray-300">Total</span>
                          <span className="text-gray-100 font-mono">{formatPrice(priceEstimate.total_price_per_hour)}/hr</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {launchError && (
                    <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 mb-6">
                      <div className="text-red-400">{launchError}</div>
                      {launchErrorType === 'capacity_exhausted' && failingRegionAgeMin != null && (
                        <div className="text-xs text-amber-300 mt-1">
                          {selectedInstance?.region} has been unavailable for ~{failingRegionAgeMin} min.
                        </div>
                      )}
                      {launchErrorType === 'quota_exceeded' && quotaModalContext && (
                        <div className="mt-3 border-t border-red-800/50 pt-3">
                          <button
                            onClick={() => setShowQuotaModal(true)}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium text-sm transition-colors"
                          >
                            Request quota increase &rarr;
                          </button>
                          <div className="text-xs text-gray-400 mt-2">
                            Prefilled with {quotaModalContext.provider.toUpperCase()} {quotaModalContext.instanceType} in {quotaModalContext.region}.
                          </div>
                        </div>
                      )}
                      {capacityAlternatives.length > 0 && (() => {
                        const allDegraded = capacityAlternatives.slice(0, 3).every(a => a.health === 'degraded');
                        return (
                          <div className="mt-3 border-t border-red-800/50 pt-3">
                            <div className="text-sm text-gray-300 mb-2">Available in other regions:</div>
                            <div className="space-y-1">
                              {capacityAlternatives.slice(0, 3).map((alt, i) => {
                                const health = alt.health ?? 'healthy';
                                const dotColor = health === 'healthy' ? 'bg-green-400' : 'bg-amber-400';
                                return (
                                  <button
                                    key={i}
                                    onClick={() => {
                                      setSelectedInstance({
                                        ...selectedInstance!,
                                        provider: alt.provider,
                                        instance_type: alt.instance_type,
                                        region: alt.region,
                                        price_per_hour_ondemand: alt.price_per_hour ?? undefined,
                                      });
                                      setLaunchError(null);
                                      setLaunchErrorType(null);
                                      setCapacityAlternatives([]);
                                      setFailingRegionAgeMin(null);
                                    }}
                                    className="w-full text-left px-3 py-2 bg-gray-800/50 hover:bg-gray-700/50 rounded text-sm transition-colors flex items-center gap-2"
                                    title={
                                      alt.last_capacity_event_age_min != null
                                        ? `Last capacity event ~${alt.last_capacity_event_age_min} min ago`
                                        : 'No recent capacity events'
                                    }
                                  >
                                    <span className={`inline-block w-2 h-2 rounded-full ${dotColor}`} />
                                    <span className="text-gray-200">{alt.instance_type}</span>
                                    <span className="text-gray-400">in</span>
                                    <span className="text-blue-400">{alt.region}</span>
                                    {alt.price_per_hour != null && (
                                      <span className="text-gray-400 ml-2">${alt.price_per_hour.toFixed(2)}/hr</span>
                                    )}
                                    {health === 'degraded' && (
                                      <span className="text-amber-400 text-xs ml-auto">degraded</span>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                            {allDegraded && (
                              <div className="text-xs text-amber-300 mt-2">
                                All alternatives have recent capacity issues — try again in a few minutes.
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                  
                  {/* Launch Progress with Cool Visualization */}
                  {launching && (
                    <div className="mb-6">
                      {/* Cool Loading Animation */}
                      <div className="flex justify-center mb-4">
                        <div className="relative w-32 h-32">
                          {/* Outer spinning ring */}
                          <div className="absolute inset-0 rounded-full border-4 border-gray-700"></div>
                          <div className="absolute inset-0 rounded-full border-4 border-t-blue-500 border-r-transparent border-b-transparent border-l-transparent animate-spin"></div>
                          
                          {/* Inner spinning ring */}
                          <div className="absolute inset-2 rounded-full border-4 border-gray-700"></div>
                          <div className="absolute inset-2 rounded-full border-4 border-t-transparent border-r-green-500 border-b-transparent border-l-transparent animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
                          
                          {/* Center GPU icon */}
                          <div className="absolute inset-0 flex items-center justify-center">
                            <svg className="w-12 h-12 text-blue-400" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M2 5a2 2 0 012-2h16a2 2 0 012 2v10a2 2 0 01-2 2h-5l-2 3v-3H4a2 2 0 01-2-2V5zm6 1v8h8V6H8zm2 2h4v4h-4V8z"/>
                            </svg>
                          </div>
                        </div>
                      </div>

                      {/* Technical Status Message */}
                      <div className="text-center mb-4">
                        <div className="text-gray-400 text-sm">
                          Provisioning {selectedInstance.provider.toUpperCase()} {selectedInstance.instance_type} instance
                        </div>
                        <div className="text-gray-500 text-xs mt-1">
                          Configuring {selectedInstance.gpu_type} GPU with CUDA drivers and ML frameworks
                        </div>
                      </div>

                      {/* Launch Steps */}
                      {launchSteps.length > 0 && (
                        <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700">
                          <div className="space-y-2">
                            {launchSteps.map((step) => (
                              <div key={step.id} className="flex items-center text-xs">
                                {step.status === 'done' && (
                                  <span className="text-green-400 mr-2 flex-shrink-0">✓</span>
                                )}
                                {step.status === 'active' && (
                                  <span className="mr-2 flex-shrink-0">
                                    <svg className="w-3 h-3 text-blue-400 animate-spin" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                    </svg>
                                  </span>
                                )}
                                {step.status === 'pending' && (
                                  <span className="text-gray-600 mr-2 flex-shrink-0">○</span>
                                )}
                                {step.status === 'error' && (
                                  <span className="text-red-400 mr-2 flex-shrink-0">✗</span>
                                )}
                                <span className={
                                  step.status === 'done' ? 'text-gray-400' :
                                  step.status === 'active' ? 'text-gray-200' :
                                  step.status === 'error' ? 'text-red-400' :
                                  'text-gray-600'
                                }>
                                  {step.label}
                                </span>
                              </div>
                            ))}
                          </div>
                          {/* Billing detail below steps */}
                          {(launchBillingInfo.prepaid || launchBillingInfo.runtime) && (
                            <div className="mt-2 pt-2 border-t border-gray-700 space-y-1">
                              {launchBillingInfo.prepaid && (
                                <div className="text-xs text-gray-400">{launchBillingInfo.prepaid}</div>
                              )}
                              {launchBillingInfo.runtime && (
                                <div className="text-xs text-gray-500">{launchBillingInfo.runtime}</div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Launch Button */}
                  <div className="flex space-x-3">
                    <button
                      onClick={() => launchInstance()}
                      disabled={launching}
                      className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 text-white rounded-lg font-medium transition-colors"
                    >
                      {launching ? 'Launching...' : 'Launch Instance'}
                    </button>
                    <button
                      onClick={() => {
                        setShowLaunchModal(false);
                        // Don't close websocket to keep monitoring in background
                      }}
                      className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-100 border border-gray-700 rounded-lg font-medium transition-colors"
                    >
                      {launching ? 'Monitor in Background' : 'Cancel'}
                    </button>
                  </div>

                  <div className="mt-4 text-xs text-gray-500">
                    * Minimum billing increment is 6 minutes. You will be charged based on actual usage.
                  </div>
                    </div>
                  </div>
                </Draggable>
              </div>
            )}

            {/* Instance Details Modal - Compact and Draggable */}
            {showInstanceModal && selectedLaunchedInstance && (
              <div className="fixed inset-0 bg-black bg-opacity-50 z-50 pointer-events-none">
                <Draggable 
                  handle=".instance-drag-handle" 
                  bounds="parent"
                  defaultPosition={{x: window.innerWidth / 2 - 300, y: window.innerHeight / 2 - 200}}
                >
                  <div className="bg-gray-900 rounded-lg shadow-2xl border border-gray-700 w-[600px] pointer-events-auto absolute">
                    {/* Header with Drag Handle */}
                    <div className="instance-drag-handle bg-gray-800 rounded-t-lg px-4 py-3 flex items-center justify-between cursor-move border-b border-gray-700">
                      <div className="flex items-center space-x-3">
                        <div className={`w-2 h-2 rounded-full ${
                          selectedLaunchedInstance.status === 'running' && selectedLaunchedInstance.terminal_ready === true ? 'bg-green-500 animate-pulse' :
                          selectedLaunchedInstance.status === 'running' ? 'bg-blue-500 animate-pulse' :
                          selectedLaunchedInstance.status === 'launching' ? 'bg-yellow-500 animate-pulse' :
                          'bg-gray-500'
                        }`} />
                        <h3 className="text-sm font-semibold text-gray-100 select-none">
                          {selectedLaunchedInstance.instance_type} • {
                            selectedLaunchedInstance.status === 'running' && selectedLaunchedInstance.terminal_ready !== true
                              ? 'Starting...'
                              : selectedLaunchedInstance.status
                          }
                        </h3>
                      </div>
                      <button
                        onClick={() => {
                          setShowInstanceModal(false);
                          setTerminalAvailable(false);
                          setCheckingTerminal(false);
                        }}
                        className="text-gray-400 hover:text-red-400 transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    {/* Compact Instance Info */}
                    <div className="p-4">
                      {/* Quick Stats Bar */}
                      <div className="flex items-center justify-between text-sm mb-4">
                        <div className="flex items-center space-x-4">
                          {(() => {
                            const liveRuntime = selectedLaunchedInstance.status === 'running' && selectedLaunchedInstance.launch_time
                              ? (Date.now() - new Date(selectedLaunchedInstance.launch_time).getTime()) / (1000 * 60 * 60)
                              : selectedLaunchedInstance.runtime_hours || 0;
                            const timeCost = selectedLaunchedInstance.status === 'running' && selectedLaunchedInstance.launch_time && selectedLaunchedInstance.hourly_cost
                              ? liveRuntime * selectedLaunchedInstance.hourly_cost
                              : selectedLaunchedInstance.total_cost || 0;
                            const minCharge = (selectedLaunchedInstance.hourly_cost || 0) * (10 / 60);
                            const liveCost = Math.max(timeCost, minCharge);
                            return (
                              <>
                                <div className="flex items-center space-x-2">
                                  <span className="text-gray-400">Runtime:</span>
                                  <span className="text-white font-medium">{liveRuntime.toFixed(2)}h</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className="text-gray-400">Cost:</span>
                                  <span className="text-green-400 font-bold">${liveCost.toFixed(2)}</span>
                                  {timeCost < minCharge && (
                                    <span className="text-[10px] text-gray-500">(min)</span>
                                  )}
                                </div>
                              </>
                            );
                          })()}
                          <div className="flex items-center space-x-2">
                            <span className="text-gray-400">Rate:</span>
                            <span className="text-yellow-400">${selectedLaunchedInstance.hourly_cost?.toFixed(3) || '0'}/hr</span>
                          </div>
                        </div>
                      </div>

                      {/* Connection Actions - Compact Row */}
                      {selectedLaunchedInstance.status === 'running' && (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between bg-gray-800 rounded-lg p-3">
                            <div className="flex items-center space-x-2">
                              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              <span className="text-gray-300 text-sm font-medium">Terminal Access</span>
                            </div>
                            {checkingTerminal ? (
                              <div className="px-3 py-1.5 bg-gray-700 text-gray-400 rounded text-sm flex items-center space-x-2">
                                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span>Checking...</span>
                              </div>
                            ) : terminalAvailable ? (
                              <button
                                onClick={() => openTerminal(selectedLaunchedInstance)}
                                className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded text-sm font-medium transition-colors"
                              >
                                Open Terminal
                              </button>
                            ) : (
                              <div className="px-3 py-1.5 bg-yellow-600/20 text-yellow-400 rounded text-sm">
                                Initializing...
                              </div>
                            )}
                          </div>
                      
                          {/* Action Buttons Row */}
                          <div className="flex items-center justify-between pt-2">
                            <div className="text-xs text-gray-400">
                              <span className="text-yellow-400">⚡</span> ${((selectedLaunchedInstance.hourly_cost || 0) / 60).toFixed(4)}/min
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => terminateInstance(selectedLaunchedInstance.instance_id)}
                                className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-medium transition-colors"
                              >
                                Terminate
                              </button>
                              <button
                                onClick={() => {
                                  setShowInstanceModal(false);
                                  setTerminalAvailable(false);
                                  setCheckingTerminal(false);
                                }}
                                className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white rounded text-sm font-medium transition-colors"
                              >
                                Close
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Non-running state */}
                      {selectedLaunchedInstance.status !== 'running' && (
                        <div className="text-center py-6">
                          <div className="text-gray-400 text-sm">
                            Instance is {selectedLaunchedInstance.status}
                            {selectedLaunchedInstance.status === 'launching' && (
                              <div className="mt-2">
                                <svg className="animate-spin h-8 w-8 mx-auto text-yellow-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => {
                              setShowInstanceModal(false);
                              setTerminalAvailable(false);
                              setCheckingTerminal(false);
                            }}
                            className="mt-4 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded text-sm font-medium transition-colors"
                          >
                            Close
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </Draggable>
              </div>
            )}

            {/* GPU Details Modal */}
            {showGPUDetailsModal && selectedGPUDetails && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-gray-900 rounded-xl border border-gray-700 shadow-2xl w-full max-w-lg mx-4">
                  <div className="px-6 py-4 flex items-center justify-between border-b border-gray-700">
                    <div className="flex items-center gap-3">
                      <span className="inline-block w-5 h-5">{getProviderIcon(selectedGPUDetails.provider)}</span>
                      <h3 className="text-lg font-semibold text-white">{selectedGPUDetails.display_name}</h3>
                    </div>
                    <button
                      onClick={() => setShowGPUDetailsModal(false)}
                      className="text-gray-400 hover:text-white transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
                    {selectedGPUDetails.short_description && (
                      <p className="text-sm text-gray-400">{selectedGPUDetails.short_description}</p>
                    )}

                    {/* GPU Specs */}
                    <div>
                      <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">GPU Specs</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-gray-800 rounded-lg p-3">
                          <div className="text-xs text-gray-500">Model</div>
                          <div className="text-sm font-medium text-white">{selectedGPUDetails.gpu_model_name ?? 'N/A'}</div>
                        </div>
                        <div className="bg-gray-800 rounded-lg p-3">
                          <div className="text-xs text-gray-500">Count</div>
                          <div className="text-sm font-medium text-white">{selectedGPUDetails.gpu_count ?? 1}x</div>
                        </div>
                        <div className="bg-gray-800 rounded-lg p-3">
                          <div className="text-xs text-gray-500">Memory per GPU</div>
                          <div className="text-sm font-medium text-white">{selectedGPUDetails.gpu_memory_per_device_gb ?? 'N/A'} GB</div>
                        </div>
                        <div className="bg-gray-800 rounded-lg p-3">
                          <div className="text-xs text-gray-500">Total GPU Memory</div>
                          <div className="text-sm font-medium text-white">{selectedGPUDetails.total_gpu_memory_gb ?? 'N/A'} GB</div>
                        </div>
                        {selectedGPUDetails.gpu_architecture && (
                          <div className="bg-gray-800 rounded-lg p-3">
                            <div className="text-xs text-gray-500">Architecture</div>
                            <div className="text-sm font-medium text-white">{selectedGPUDetails.gpu_architecture}</div>
                          </div>
                        )}
                        {selectedGPUDetails.fp32_tflops && (
                          <div className="bg-gray-800 rounded-lg p-3">
                            <div className="text-xs text-gray-500">FP32 Performance</div>
                            <div className="text-sm font-medium text-white">{selectedGPUDetails.fp32_tflops} TFLOPS</div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* System Specs */}
                    <div>
                      <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">System</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-gray-800 rounded-lg p-3">
                          <div className="text-xs text-gray-500">Instance Type</div>
                          <div className="text-sm font-medium text-white">{selectedGPUDetails.provider_instance_id}</div>
                        </div>
                        <div className="bg-gray-800 rounded-lg p-3">
                          <div className="text-xs text-gray-500">Region</div>
                          <div className="text-sm font-medium text-white">{selectedGPUDetails.region}</div>
                        </div>
                        <div className="bg-gray-800 rounded-lg p-3">
                          <div className="text-xs text-gray-500">vCPUs</div>
                          <div className="text-sm font-medium text-white">{selectedGPUDetails.vcpus ?? 'N/A'}</div>
                        </div>
                        <div className="bg-gray-800 rounded-lg p-3">
                          <div className="text-xs text-gray-500">System Memory</div>
                          <div className="text-sm font-medium text-white">{selectedGPUDetails.system_memory_gib ?? 'N/A'} GiB</div>
                        </div>
                        {selectedGPUDetails.network_bandwidth_gbps && (
                          <div className="bg-gray-800 rounded-lg p-3 col-span-2">
                            <div className="text-xs text-gray-500">Network Bandwidth</div>
                            <div className="text-sm font-medium text-white">{selectedGPUDetails.network_bandwidth_gbps} Gbps</div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Pricing */}
                    <div>
                      <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Pricing</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-gray-800 rounded-lg p-3">
                          <div className="text-xs text-gray-500">On-Demand</div>
                          <div className="text-sm font-bold text-white">
                            {selectedGPUDetails.on_demand_price_per_hour != null
                              ? `$${selectedGPUDetails.on_demand_price_per_hour.toFixed(2)}/hr`
                              : 'N/A'}
                          </div>
                        </div>
                        <div className="bg-gray-800 rounded-lg p-3">
                          <div className="text-xs text-gray-500">Spot</div>
                          <div className="text-sm font-bold text-green-400">
                            {selectedGPUDetails.spot_price_per_hour != null
                              ? `$${selectedGPUDetails.spot_price_per_hour.toFixed(2)}/hr`
                              : 'N/A'}
                          </div>
                        </div>
                        {selectedGPUDetails.total_price_per_hour != null && (
                          <div className="bg-gray-800 rounded-lg p-3">
                            <div className="text-xs text-gray-500">Total (incl. disk)</div>
                            <div className="text-sm font-medium text-white">
                              ${selectedGPUDetails.total_price_per_hour.toFixed(2)}/hr
                            </div>
                          </div>
                        )}
                        {selectedGPUDetails.price_performance_ratio != null && (
                          <div className="bg-gray-800 rounded-lg p-3">
                            <div className="text-xs text-gray-500">Price/Performance</div>
                            <div className="text-sm font-medium text-white">
                              {selectedGPUDetails.price_performance_ratio.toFixed(2)}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Recommended Workloads */}
                    {selectedGPUDetails.recommended_workloads.length > 0 && (
                      <div>
                        <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">Recommended Workloads</h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedGPUDetails.recommended_workloads.map((workload) => (
                            <span
                              key={workload}
                              className="px-2.5 py-1 text-xs font-medium bg-gray-800 text-gray-300 rounded-md border border-gray-700"
                            >
                              {workload}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Quota Status */}
                    {selectedGPUDetails.quota_available != null && (
                      <div className="flex items-center gap-2 text-sm">
                        <div className={`w-2 h-2 rounded-full ${selectedGPUDetails.quota_available ? 'bg-green-500' : 'bg-red-500'}`} />
                        <span className="text-gray-400">
                          {selectedGPUDetails.quota_available
                            ? `Quota available (${selectedGPUDetails.quota_remaining ?? '?'} remaining)`
                            : 'Quota limit reached'}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="px-6 py-4 border-t border-gray-700 flex justify-end gap-3">
                    <button
                      onClick={() => setShowGPUDetailsModal(false)}
                      className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
                    >
                      Close
                    </button>
                    <button
                      onClick={() => {
                        setShowGPUDetailsModal(false);
                        const legacyInstance: GPUInstance = {
                          provider: selectedGPUDetails.provider,
                          region: selectedGPUDetails.region,
                          instance_type: selectedGPUDetails.provider_instance_id,
                          instance_name: selectedGPUDetails.gpu_model_name || 'GPU Instance',
                          vcpus: selectedGPUDetails.vcpus || 0,
                          memory_gb: selectedGPUDetails.system_memory_gib || 0,
                          gpu_count: selectedGPUDetails.gpu_count || 1,
                          gpu_type: selectedGPUDetails.gpu_model_name || 'Unknown',
                          gpu_memory_gb: selectedGPUDetails.gpu_memory_per_device_gb || 0,
                          price_per_hour_ondemand: selectedGPUDetails.on_demand_price_per_hour || undefined,
                          price_per_hour_spot: selectedGPUDetails.spot_price_per_hour || undefined,
                          status: 'available',
                          use_cases: selectedGPUDetails.recommended_workloads || []
                        };
                        setSelectedInstance(legacyInstance);
                        setShowLaunchModal(true);
                      }}
                      className="px-4 py-2 bg-white text-black text-sm font-medium rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      Launch Instance
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Background Progress Notification */}
            {showProgressNotification && !showLaunchModal && launchSteps.some(s => s.status !== 'pending') && (
              <div className="fixed bottom-4 right-4 z-50 w-80">
                <div className="bg-gray-900 border border-gray-700 rounded-lg shadow-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <div className="relative w-6 h-6 mr-2">
                        <div className="absolute inset-0 rounded-full border-2 border-gray-700"></div>
                        <div className="absolute inset-0 rounded-full border-2 border-t-blue-500 border-r-transparent border-b-transparent border-l-transparent animate-spin"></div>
                      </div>
                      <span className="text-white font-medium text-sm">Launching Instance</span>
                    </div>
                    <button
                      onClick={() => setShowProgressNotification(false)}
                      className="text-gray-400 hover:text-white"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  <div className="space-y-1.5">
                    {launchSteps.map((step) => (
                      <div key={step.id} className="flex items-center text-xs">
                        {step.status === 'done' && <span className="text-green-400 mr-2">✓</span>}
                        {step.status === 'active' && (
                          <span className="mr-2">
                            <svg className="w-3 h-3 text-blue-400 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                          </span>
                        )}
                        {step.status === 'pending' && <span className="text-gray-600 mr-2">○</span>}
                        {step.status === 'error' && <span className="text-red-400 mr-2">✗</span>}
                        <span className={
                          step.status === 'done' ? 'text-gray-400' :
                          step.status === 'active' ? 'text-gray-200' :
                          step.status === 'error' ? 'text-red-400' :
                          'text-gray-600'
                        }>
                          {step.label}
                        </span>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => {
                      setShowLaunchModal(true);
                      setShowProgressNotification(false);
                    }}
                    className="mt-3 text-xs text-blue-400 hover:text-blue-300"
                  >
                    View Details →
                  </button>
                </div>
              </div>
            )}

            {/* Render active terminal windows */}
            {Array.from(terminalSessions.entries()).map(([sessionId, session]) => {
              return (
                <Terminal
                  key={sessionId}
                  sessionId={session.sessionId}
                  instanceId={session.instanceId}
                  provider={session.provider}
                  instanceType={session.instanceType}
                  region={session.region}
                  onClose={() => {
                    setTerminalSessions(prev => {
                      const next = new Map(prev);
                      next.delete(sessionId);
                      return next;
                    });
                  }}
                />
              );
            })}

            {/* Connection Modal */}
            <ConnectionModal />

            {/* Quota Request Modal — opens from launch-error CTA when error_type=quota_exceeded */}
            <QuotaRequestModal
              isOpen={showQuotaModal}
              onClose={() => setShowQuotaModal(false)}
              prefillProvider={quotaModalContext?.provider}
              prefillInstanceType={quotaModalContext?.instanceType}
              prefillRegion={quotaModalContext?.region}
              prefillDisplayName={quotaModalContext?.displayName}
            />
          </div>
    </div>
  );
}
